import React, { useEffect, useRef, useState } from "react";
import { sendJacadaRequest } from "../../api";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import { useLocale } from "../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import CONSTANTS, {
  FUEL_SERVICE_ID,
  TIRE_CHANGE_ID,
  DISPLAY_TRIAGE_STEPS_STATUSES,
  EPCS_STATUS_CARD_ERROR,
  TOW,
  JJ_AND_AMC,
  TOW_SERVICE_ID,
  VANDALISM,
  ACCIDENT,
  validCustomerType,
  TIRE_DAMAGE,
  TIRE_STANDARD_WITHOUT_SPARE
} from "../../app-consts/appConstants";
import { TimelineMax } from "gsap";
import { iconToCheckAnimation } from "./animationHelper";
import { Button } from "mesh-component-library";
import {
  CustomerSupportIcon,
  CreditCardIcon,
  CarIcon,
  ReportIcon,
} from "../../assets";
import Icon from "../icon";
import { SelectablePanel } from "../selectable-panel";
import renderIcon from "../../utilities/render-icon";
import {
  colorTranslatedText,
  serviceTranslatedText,
} from "../../utils/translatedTextUtils";
import titleCase from "../../utils/titleCase";
import { sendVehicleToJacada } from "../../jacada/sendJacadaEventData";
import {
  generatePath,
  generatePathWithSearchParams,
} from "../../utilities/RedirectUtils";
import { datadogRum } from "@datadog/browser-rum";
import Spinner from "../spinner";
import { isScheduleService } from "../../utils/serviceRequestPayloadUtils";
import { getVaChat, isABGPartner,  shouldAllowRescueSubmit } from "../../utils";
import { ChatClosureReminderModal } from "./components/ChatClosureReminderModal";
import {
  refreshReducersToInitial as refreshReducers,
  saveRescueDetailsRetrieveResultsRequest
} from "../../action";
import clearCache from "../../utils/clearCache";
import { PartnerDetails } from "../../types";
import { Vehicle } from "../../types/CommonBenefits";

export type ServiceDetailsOverlayProps = {
  commonBenefits: any;
  destination: any;
  epcsStatus: any;
  history: any;
  incidentReportFormComplete: boolean;
  isPepPartner: boolean;
  logChatIntakeResults: (message: string) => void;
  makeModel: any;
  omniStreamlined: boolean;
  partnerCode: string;
  partnerDetails: PartnerDetails;
  refreshReducersToInitial: () => void;
  serviceInfo: any;
  serviceRequestPayload: any;
  submitRequest: any;
  updatePpuFuelPrice: (price: number) => void;
  submitServiceRequest: (data: any) => void;
  updateServiceTypeOptions: (options: any) => void;
  updateVehicle: (vehicle: any) => void;
  updatedEpcsStatus: () => void;
  towReasonId: string;
  replacementRentalCarFromRedux: string;
  needsTransportFromRedux: string;
  customerType: string;
  schedulingEnabled: boolean;
  readyToSubmitRescue: boolean;
};

const hasRequiredVehicleDetails = (vehicle?: Vehicle) => {
  return [vehicle?.color, vehicle?.make, vehicle?.model, vehicle?.year].every(
    (value) => !!String(value || "").trim()
  );
};

export const ServiceDetailsOverlay = ({
  commonBenefits,
  destination,
  epcsStatus,
  isPepPartner,
  makeModel,
  partnerCode,
  partnerDetails,
  serviceInfo,
  serviceRequestPayload,
  submitServiceRequest,
  updatedEpcsStatus,
  updateVehicle,
  updateServiceTypeOptions,
  updatePpuFuelPrice,
  submitRequest,
  incidentReportFormComplete,
  logChatIntakeResults,
  omniStreamlined,
  refreshReducersToInitial,
  towReasonId,
  replacementRentalCarFromRedux,
  needsTransportFromRedux,
  customerType,
  schedulingEnabled,
  readyToSubmitRescue,
  ...props
}: ServiceDetailsOverlayProps) => {
  const getTranslatedText = useLocale();
  const dispatch = useDispatch();

  const [showSpinner, setShowSpinner] = useState(false);
  const [paymentText, setPaymentText] = useState(
    getTranslatedText(TRANSLATION_CONSTANTS.CONTACT_PAYMENT)
  );
  const [formComplete, setFormComplete] = useState(false);
  const [vehicleDetailsText, setVehicleDetailsText] = useState(
    getTranslatedText(TRANSLATION_CONSTANTS.CONFIRM_VEHICLE)
  );
  const [incidentReportText, setIncidentReportText] = useState(
    getTranslatedText(TRANSLATION_CONSTANTS.COMPLETE_INCIDENT_REPORT)
  );
  const [openChatReminderModal, setOpenChatReminderModal] = useState(false);
  const carIconAnimation = new TimelineMax({ paused: true });
  const creditCardAnimation = new TimelineMax({ paused: true });
  const reportIconAnimation = new TimelineMax({ paused: true });
  const hasMounted = useRef(false);
  const needsReplacementRental = isABGPartner(partnerCode) && validCustomerType(customerType) && serviceInfo.id === TOW_SERVICE_ID;
  const replacementRentalAnswered = needsReplacementRental
      ? !!replacementRentalCarFromRedux
      : true;

  const shouldDisplayIncidentReport =
    isABGPartner(partnerCode) &&
    (
      serviceInfo.id === TIRE_CHANGE_ID ||
      (serviceInfo.id === TOW_SERVICE_ID &&
        [ACCIDENT, VANDALISM, TIRE_DAMAGE, TIRE_STANDARD_WITHOUT_SPARE, "abandoned", "glass_damage"].includes(towReasonId))
    );


  const shouldDisplayRescueButton = shouldAllowRescueSubmit(partnerCode, serviceInfo?.service, serviceRequestPayload?.commentRequest?.comment, serviceRequestPayload?.serviceRequest?.destinationAddress, readyToSubmitRescue);
  
  const requiresAdditionalTransportationStep =
    (replacementRentalCarFromRedux === "Yes" ||
      needsTransportFromRedux === "Yes");
  const shouldUseContinueCta = requiresAdditionalTransportationStep && formComplete;

  // Check if we change payment button text
  useEffect(() => {
    if (
      isPepPartner ||
      commonBenefits.benefits ||
      partnerDetails.experience?.passThruEnabled
    ) {
      setPaymentText(getTranslatedText(TRANSLATION_CONSTANTS.CONFIRM_CONTACT));
    }

  }, []);

  // Check vehicle info
  useEffect(() => {
    let vehicleInfo: any = {};
    if (
      serviceInfo.id === FUEL_SERVICE_ID &&
      commonBenefits?.vehicles?.length === 1 &&
      commonBenefits.vehicles[0].color &&
      commonBenefits.vehicles[0].fuelType &&
      commonBenefits.vehicles[0].fuelType?.toLowerCase() !== "null" &&
      !(
        serviceRequestPayload.serviceRequest.vehicle ||
        serviceRequestPayload.serviceRequest.vehicles
      )
    ) {
      vehicleInfo = { ...commonBenefits.vehicles[0] };
      updateServiceTypeOptions({
        gas: titleCase(vehicleInfo.fuelType),
      });
      updateVehicle(vehicleInfo);
    } else {
      vehicleInfo =
        serviceRequestPayload.serviceRequest.vehicle ||
        serviceRequestPayload.serviceRequest.vehicles;

      if (
        serviceInfo.id === FUEL_SERVICE_ID &&
        !serviceRequestPayload.serviceRequest.serviceTypeOptions?.gas
      ) {
        vehicleInfo = {};
      }
    }

    if (hasRequiredVehicleDetails(vehicleInfo)) {
      if (replacementRentalAnswered) {
        carIconAnimation.play(0, false);
      }

      setVehicleDetailsText(
        `${vehicleInfo?.year} ${getTranslatedText(
          colorTranslatedText(titleCase(vehicleInfo?.color))
        )} ${vehicleInfo?.make} ${vehicleInfo?.model}`
      );

      sendVehicleToJacada(vehicleInfo);
      if (omniStreamlined && serviceInfo.service !== "gas" && !hasMounted.current) {
        dispatch(saveRescueDetailsRetrieveResultsRequest({
          serviceType: serviceInfo.service,
          history: props.history,
          dispatcher : () => dispatch(refreshReducersToInitial())
        }));
        hasMounted.current = true;
      }
    }
  }, [commonBenefits]);

  // Check service request payload for EPCS to display name on page
  useEffect(() => {
    if (
      epcsStatus.completed &&
      serviceRequestPayload.serviceRequest.firstName
    ) {
      let displayName = titleCase(
        serviceRequestPayload.serviceRequest.firstName +
          " " +
          serviceRequestPayload.serviceRequest.lastName
      );
      setPaymentText(displayName);
      creditCardAnimation.play(0, false);
    }
  }, [serviceRequestPayload]);

  // Mark Incident Report Form complete
  useEffect(() => {
    if (incidentReportFormComplete) {
      setIncidentReportText(
        getTranslatedText(TRANSLATION_CONSTANTS.COMPLETED_INCIDENT_REPORT)
      );
      reportIconAnimation.play(0, false);
    }
    iconToCheckAnimation(
      carIconAnimation,
      creditCardAnimation,
      reportIconAnimation
    );
  }, []);

  // Mark form as ready to submit
  useEffect(() => {
    const {
      vehicle,
      vehicles,
      firstName,
      lastName,
      contactNumber,
      CCardInfo,
      ppuFlag,
      serviceTypeOptions,
      email,
    } = serviceRequestPayload.serviceRequest;
    let rescueVehicle = vehicle || vehicles;
    let vehicleInfoFilled = hasRequiredVehicleDetails(rescueVehicle);
    let personalInfoFilled = !!firstName && !!lastName && !!contactNumber;
    if (ppuFlag === "Y" && partnerDetails.experience?.ppuEnabled) {
      personalInfoFilled =
        personalInfoFilled &&
        email &&
        CCardInfo?.maskedCardNumber &&
        CCardInfo?.billingZipCode;
    }
    if (serviceInfo.id === FUEL_SERVICE_ID) {
      vehicleInfoFilled = vehicleInfoFilled && serviceTypeOptions?.gas;
    }
    const formReady = !shouldDisplayIncidentReport
      ? vehicleInfoFilled && personalInfoFilled && replacementRentalAnswered
      : incidentReportFormComplete && vehicleInfoFilled && personalInfoFilled && replacementRentalAnswered;
    setFormComplete(formReady);
  }, [serviceRequestPayload]);

  useEffect(() => {
    redirectPage();
    if (
      !hasMounted.current &&
      omniStreamlined &&
      serviceInfo.service === "gas" &&
      (serviceRequestPayload.serviceRequest.vehicle ||
        serviceRequestPayload.serviceRequest.vehicles) &&
      serviceRequestPayload.serviceRequest.serviceTypeOptions
    ) {
      dispatch(saveRescueDetailsRetrieveResultsRequest({
        serviceType: serviceInfo.service,
        history: props.history,
        dispatcher: () => dispatch(refreshReducersToInitial())
      }));
      hasMounted.current = true;
    }
  });

  const handleConfirmVehicleClick = () => {
    if (commonBenefits.benefits) {
      props.history.push(generatePath("confirm-vehicle"));
      datadogRum.addAction("Vehicle Select Modal Clicked", undefined);
    } else {
      props.history.push(generatePath("add-vehicle"));
      datadogRum.addAction("Vehicle Information Modal Clicked", undefined);
    }
  };

  const toggleChatReminderModal = () => {
    setOpenChatReminderModal(!openChatReminderModal);
    getVaChat().open();
  };

  const handleSubmit = () => {
    const hasOpenChat =
      document.getElementsByClassName("va_chat_open").length > 0;
    const hasExistingChat =
      document.getElementsByClassName("va_chat_resume").length > 0;

    if (!hasOpenChat && !hasExistingChat) {
      if (
        commonBenefits.vehicles?.find(
          (vehicle) =>
            vehicle?.vehicleID ===
            serviceRequestPayload.serviceRequest?.vehicles?.consumervehicleid
        )?.suspendIndicator === "Y"
      ) {
        updatePpuFuelPrice(0);
      }
      logChatIntakeResults("Submit Rescue Clicked");
      if (showSpinner === false) {
        setShowSpinner(true);
      }
      serviceRequestPayload.history = props.history;
      submitServiceRequest(serviceRequestPayload);
    } else {
      setOpenChatReminderModal(true);
    }
    if (requiresAdditionalTransportationStep) {
      props.history.push(generatePath("CallUs"));
      datadogRum.addAction("Redirected to Give Us a Call page from Service Details Overlay");
      return;
    }
  };

  const handleIncidentReportClick = () => {
    props.history.push(generatePath("incident-report"));
  };

  const openPaymentModal = () => {
    props.history.push(generatePath("payment"));
  };

  const amountDue = () => {
    if (
      DISPLAY_TRIAGE_STEPS_STATUSES.includes(commonBenefits.status) &&
      commonBenefits?.status?.toUpperCase() ===
        CONSTANTS.MUST_SHOW_NO_BENEFITS_PRICING_INFO
    ) {
      return serviceInfo?.displayCost ? "$" + serviceInfo?.displayCost : "";
    } else {
      if (
        serviceRequestPayload.hasPepSession &&
        serviceInfo.serviceText === TOW
      ) {
        return "$" + destination?.towInfo?.totalTowCost;
      } else {
        const transactionAmount =
          serviceRequestPayload.serviceRequest.CCardInfo?.transactionAmount;
        return transactionAmount ? "$" + transactionAmount : "";
      }
    }
  };

  const redirectPage = () => {
    if(commonBenefits?.errorMessage === CONSTANTS.SERVICE_REQUEST_DENIED
      && JJ_AND_AMC.includes(partnerCode)){
      props.history.push(generatePath("ServiceTransition"));
      return;
    }
    if (submitRequest.success) {
      let sessionId, callId, requestId, usageId;
      if (omniStreamlined) {
        sessionId = localStorage.getItem("benefitSession") || serviceRequestPayload.sessionId;
        callId = sessionId;
        usageId =
          localStorage.getItem("usageId") || serviceRequestPayload.usageId;
      } else {
        sessionId = submitRequest.response.sessionId;
        callId = submitRequest.response.callId;
        requestId = submitRequest.response.requestId;
        usageId = submitRequest.response.usageId;
      }
      if (partnerDetails.route === "OmniAssist") {
        sendJacadaRequest({
          externalSystemId: localStorage.getItem("conversationId"),
          events: [
            {
              name: "call_id",
              value: callId,
            },
          ],
        });
      }

      localStorage.setItem("requestId", requestId);
      localStorage.setItem("submitRequest", JSON.stringify(submitRequest));

      if (isScheduleService(serviceRequestPayload) && schedulingEnabled) {
        props.history.push(
          `/appointment-confirmation?${new URLSearchParams({ usageId: usageId ?? "", partnerCode: partnerCode ?? "", callId: callId ?? "" }).toString()}`
        );
      } else if (omniStreamlined && partnerCode && usageId && sessionId && !sessionId.includes("-")) {
        props.history.push(
          generatePathWithSearchParams(
            `/wd/l/${partnerCode}/${sessionId}/${usageId}`
          )
        );
      } else if(callId && requestId && usageId && sessionId && sessionId.includes("-")) {
        props.history.push(
          generatePath(`tracker/${sessionId}/${callId}/${requestId}/${usageId}`)
        );
      } else {
        clearCache(() => dispatch(refreshReducers()));
        datadogRum.addAction("Recovered from tracker redirection from ServiceDetailsOverlay");
        return;
      }
    }
    if (makeModel.error || submitRequest.fail) {
      let failureType;
      let response;
      if (makeModel.error) {
        failureType = "HowEmbarrassing-MakeModel";
        response = makeModel.payload;
      } else if (submitRequest.fail) {
        failureType = "HowEmbarrassing-ServiceRequest";
        response = submitRequest.response;
      }
      datadogRum.addError(
        `Failure Type: ${failureType}, Response: ${response}`
      );
      props.history.push(generatePath("sitedown"));
    }
  };

  return (
      <div className="vehiclepaycontainer" id={isABGPartner(partnerCode) ? "service-details-overlay-abg" : ""}>
        {showSpinner && <Spinner />}
        <ChatClosureReminderModal
          openModal={openChatReminderModal}
          toggleModal={toggleChatReminderModal}
        />
        <div className="l-grid__col">
          <div className="c-selected-service">
            <Icon
              className="c-selected-service__icon"
              icon={renderIcon(serviceInfo.serviceText)}
              size={1.5}
              color="brand"
            />
            <h5 className="c-selected-service__type">
              {getTranslatedText(
                serviceTranslatedText(serviceInfo.serviceText)
              )}
            </h5>
            <h4 className="c-selected-service__price">{amountDue()} </h4>
          </div>
        </div>

        <div className="l-grid__col">
          <SelectablePanel
            text={vehicleDetailsText}
            id="vehicle-info"
            className="vehiclepanel"
            name="radiooptions"
            ariaLabel={vehicleDetailsText}
            value="vehiclepanel"
            type="button"
            role="link"
            serviceClass="vehicle-item-1"
            metaMessage=""
            location={true}
            onClick={() => {
              handleConfirmVehicleClick();
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                handleConfirmVehicleClick();
              }
            }}
          >
            <CarIcon />
            <Icon
              icon="chevron"
              color="brand"
              size={1.2}
              className="icon-rotation-reverse vehicle-item-3"
            />
          </SelectablePanel>
        </div>

        <div data-dd-privacy="mask" className="l-grid__col">
          <SelectablePanel
            dataTestId="contact-or-payment-info"
            text={paymentText}
            id="payment-info"
            className="paymentpanel"
            name="radiooptions"
            ariaLabel="Contact or Payment Info"
            value="paymentpanel"
            type="button"
            role="link"
            serviceClass="vehicle-item-1"
            errorMessage=""
            metaMessage=""
            location={true}
            onClick={() => {
              openPaymentModal();
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                openPaymentModal();
              }
            }}
          >
            {isPepPartner || commonBenefits.benefits ? (
              <CustomerSupportIcon />
            ) : (
              <CreditCardIcon />
            )}
            <Icon
              icon="chevron"
              color="brand"
              size={1.2}
              className="icon-rotation-reverse vehicle-item-3"
            />
          </SelectablePanel>
        </div>
        <div className="l-grid__col">
          {shouldDisplayIncidentReport && (
            <SelectablePanel
              text={incidentReportText}
              name="radiooptions"
              ariaLabel="Complete Incident Report"
              value="incidentReport"
              type="button"
              role="link"
              serviceClass="vehicle-item-1"
              errorMessage=""
              metaMessage=""
              location={true}
              onClick={() => {
                handleIncidentReportClick();
              }}
              onKeyDown={(e) => {
                if (e.keyCode === 13) {
                  handleIncidentReportClick();
                }
              }}
              id="incident-report"
            >
              <ReportIcon />
              <Icon
                icon="chevron"
                color="brand"
                size={1.2}
                className="icon-rotation-reverse vehicle-item-3"
              />
            </SelectablePanel>
          )}
          <br />
          {shouldDisplayRescueButton && <Button
            size="lg"
            hasUpgrade
            id="submit-request"
            disabled={!formComplete || epcsStatus?.response?.paymentStatus === EPCS_STATUS_CARD_ERROR}
            onClick={() => handleSubmit()}
            utils={{
              fullWidth: true,
            }}
          >
            {getTranslatedText(
              shouldUseContinueCta
                ? TRANSLATION_CONSTANTS.CONTINUE
                : TRANSLATION_CONSTANTS.SUBMIT_REQUEST
            )}
          </Button>}
        </div>
      </div>

  );
};
